import { Skeleton } from "@mui/material";
import { IconBookmark, IconBookmarkFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import grainImg from "../../assets/images/collection-cover-grain.svg";
import PlaylistThumbnail from "./PlaylistThumbnail";

type DesktopCollectionCardPlaylist = {
  title: string;
  images: { url: string; sizes: { medium: { file: string } } | null }[];
  metrics: { articles: number; journals: number };
  featuredArticle: { title: string; journalName: string | null } | null;
};

/**
 * A collection on desktop (Figma "CollectionCard"): its cover, the title and
 * the counts, in the grids and bands of the desktop pages. The cover is the
 * collection's image under the paper of its first article (Figma
 * ".CollectionCover", size L); a collection with no article yet has no paper.
 *
 * The whole card opens the collection. The bookmark, when the reader can save
 * the collection, is a separate button above it.
 */
export function DesktopCollectionCard({
  playlist,
  onOpen,
  bookmark,
}: {
  playlist: DesktopCollectionCardPlaylist;
  onOpen: () => void;
  bookmark: { isSaved: boolean; onToggle: () => void } | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const image = playlist.images.at(0);
  const { featuredArticle } = playlist;

  return (
    <div className="group relative flex flex-col gap-2.5 font-dm-sans">
      {/* The card's own target, laid over it so the bookmark can sit inside.
          First, so Tab reaches the card before its bookmark. */}
      <button
        type="button"
        aria-label={playlist.title}
        onClick={onOpen}
        className="absolute inset-0 z-1 cursor-pointer rounded-xl border-none bg-transparent p-0 focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-focus-ring"
      />
      <div className="relative h-46 w-full shrink-0 overflow-hidden rounded-xl bg-ds-slate-100">
        {image === undefined ? (
          <PlaylistThumbnail backgroundColor={null} />
        ) : (
          // Wider than the cover, as the design crops it: the bottom of the
          // image shows, and the blur does not fade at the edges. The filters
          // are the design's image adjustments (exposure, contrast, saturation).
          <div
            aria-hidden
            className="absolute -inset-x-10 -top-15 -bottom-14 bg-cover bg-bottom blur-collection-cover brightness-110 contrast-85 saturate-120"
            style={{
              backgroundImage: `url(${
                image.sizes === null ? image.url : image.sizes.medium.file
              })`,
            }}
          />
        )}
        <div
          aria-hidden
          className="absolute inset-0 opacity-8"
          style={{ backgroundImage: `url(${grainImg})` }}
        />
        {featuredArticle === null ? null : (
          <>
            <div className="absolute -right-9 -bottom-5.5 h-32 w-56 -rotate-4 rounded-lg bg-ds-white/70" />
            <div className="absolute top-22 -right-12.5 flex w-58.5 rotate-2 flex-col gap-1 rounded-lg bg-ds-white p-3.5 shadow-collection-paper transition duration-300 group-hover:-translate-y-0.5 group-hover:rotate-3 group-hover:shadow-collection-paper-hover">
              {featuredArticle.journalName === null ? null : (
                <p className="truncate text-sm font-medium text-ds-mint-800">
                  {featuredArticle.journalName}
                </p>
              )}
              <p className="line-clamp-3 text-desktop-meta font-semibold text-ds-black">
                {featuredArticle.title}
              </p>
            </div>
          </>
        )}
      </div>
      <p className="line-clamp-3 text-desktop-card-title font-semibold text-ds-black">
        {playlist.title}
      </p>
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate text-desktop-meta text-ds-slate-600">
          {playlist.metrics.journals === 0
            ? t("{{count}} articles", { count: playlist.metrics.articles })
            : `${t("{{count}} articles", { count: playlist.metrics.articles })} · ${t("{{count}} journals", { count: playlist.metrics.journals })}`}
        </p>
        {bookmark === null ? null : (
          <button
            type="button"
            aria-label={bookmark.isSaved ? t("Saved") : t("Save")}
            aria-pressed={bookmark.isSaved}
            onClick={bookmark.onToggle}
            className="relative z-2 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent p-0 text-ds-slate-700 hover:text-ds-black focus-visible:shadow-focus-ring focus-visible:outline-none"
          >
            {bookmark.isSaved ? (
              <IconBookmarkFilled className="size-5" />
            ) : (
              <IconBookmark className="size-5" stroke={1.5} />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export function DesktopCollectionCardSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2.5">
      <Skeleton
        variant="rectangular"
        className="h-46 w-full shrink-0 rounded-xl"
        sx={{ bgcolor: "rgba(0, 0, 0, 0.08)" }}
      />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width="40%" />
    </div>
  );
}
